import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Menu, Heart } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { useFavorites } from '../context/FavoritesContext';

/**
 * PageHeader — single header used on all main pages.
 *
 * Variants drawn from the mockup screens:
 *   - Home (mockup 1):       title + subtitle + bell (right) + menu (right)
 *   - Home-logo (Sprint 20): logo + wordmark "PriceMap" (clickable, refresh)
 *                            + subtitle + bell + menu
 *   - Search (mockup 2):     back (left) + centered title + filter (right)
 *   - Discounts (mockup 3):  back (left) + centered title only
 *
 * Props:
 *   title       — main heading text (required for 'home'/'sub')
 *   subtitle    — optional smaller line under the title (home / home-logo)
 *   variant     — 'home' | 'home-logo' | 'sub'
 *   onLogoClick — click handler for the logo+wordmark (home-logo only)
 *   onMenuClick — when present, render menu icon on the right
 *   onFavClick  — when present, render favorites (heart) icon on the right
 *   rightSlot   — custom right-side button (e.g. filter icon on /search)
 */
export function PageHeader({
  title,
  subtitle,
  variant = 'sub',
  onLogoClick,
  onMenuClick,
  onFavClick,
  rightSlot,
}) {
  const navigate = useNavigate();
  const isHome = variant === 'home';
  const isHomeLogo = variant === 'home-logo';
  const { totalUnread } = useNotifications();
  const { favorites } = useFavorites();
  const favCount = favorites.length;

  return (
    <header
      className={`sticky top-0 z-40 bg-[#fafafa]/90 backdrop-blur supports-[backdrop-filter]:bg-[#fafafa]/75 ${
        isHome || isHomeLogo ? '' : 'border-b border-slate-100'
      }`}
      data-testid="page-header"
    >
      {isHomeLogo ? (
        // Home-logo variant (Sprint 20) — logo + "PriceMap" wordmark on the
        // left, bell + menu on the right, optional subtitle on a second row.
        // Logo+wordmark is one tappable target that refreshes home.
        <div className="px-5 pt-4 pb-3">
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={onLogoClick}
              className="flex items-center gap-2 active:scale-95 transition-transform min-w-0"
              data-testid="header-logo"
              aria-label="PriceMap მთავარი"
            >
              <img
                src="/pricemap-logo.svg"
                alt=""
                className="w-8 h-8 shrink-0"
                draggable="false"
              />
              <span className="text-[20px] font-bold text-slate-900 tracking-tight leading-none">
                PriceMap
              </span>
            </button>

            <div className="flex items-center gap-2 shrink-0">
              {onFavClick && (
                <button
                  onClick={onFavClick}
                  className="relative w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 active:scale-95 flex items-center justify-center transition-all"
                  data-testid="header-fav"
                  aria-label="ფავორიტები"
                >
                  <Heart className="w-5 h-5 text-slate-600" strokeWidth={1.7} />
                  {favCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-red-500 ring-2 ring-white flex items-center justify-center px-1 text-[9px] font-bold text-white leading-none">
                      {favCount > 99 ? '99+' : favCount}
                    </span>
                  )}
                </button>
              )}
              {onMenuClick && (
                <button
                  onClick={onMenuClick}
                  className="relative w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 active:scale-95 flex items-center justify-center transition-all"
                  data-testid="header-menu"
                  aria-label="მენიუ"
                >
                  <Menu className="w-5 h-5 text-slate-600" strokeWidth={1.7} />
                  {totalUnread > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
                  )}
                </button>
              )}
            </div>
          </div>

          {subtitle && (
            <p
              className="mt-2 text-[13px] text-slate-500 font-normal"
              data-testid="page-subtitle"
            >
              {subtitle}
            </p>
          )}
        </div>
      ) : isHome ? (
        // Home variant — large stacked title + fav top-right
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h1
                className="text-[28px] leading-[1.1] font-bold text-slate-900 tracking-tight"
                data-testid="page-title"
              >
                {title}
              </h1>
              {subtitle && (
                <p className="mt-1.5 text-[13px] text-slate-500 font-normal">
                  {subtitle}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {onFavClick && (
                <button
                  onClick={onFavClick}
                  className="relative w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 active:scale-95 flex items-center justify-center transition-all"
                  data-testid="header-fav"
                  aria-label="ფავორიტები"
                >
                  <Heart className="w-5 h-5 text-slate-600" strokeWidth={1.7} />
                  {favCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-red-500 ring-2 ring-white flex items-center justify-center px-1 text-[9px] font-bold text-white leading-none">
                      {favCount > 99 ? '99+' : favCount}
                    </span>
                  )}
                </button>
              )}
              {onMenuClick && (
                <button
                  onClick={onMenuClick}
                  className="relative w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 active:scale-95 flex items-center justify-center transition-all"
                  data-testid="header-menu"
                  aria-label="მენიუ"
                >
                  <Menu className="w-5 h-5 text-slate-600" strokeWidth={1.7} />
                  {totalUnread > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        // Sub variant — compact 3-column header (back / title / right)
        <div className="grid grid-cols-[40px_1fr_40px] items-center px-4 h-14 gap-2">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 active:scale-95 flex items-center justify-center transition-all"
            data-testid="header-back"
            aria-label="უკან"
          >
            <ChevronLeft className="w-5 h-5 text-slate-700" strokeWidth={1.8} />
          </button>

          <h1
            className="text-[16px] font-semibold text-slate-900 text-center truncate"
            data-testid="page-title"
          >
            {title}
          </h1>

          <div className="w-10 h-10 flex items-center justify-center">
            {rightSlot ?? (
              onMenuClick ? (
                <button
                  onClick={onMenuClick}
                  className="relative w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 active:scale-95 flex items-center justify-center transition-all"
                  data-testid="header-menu"
                  aria-label="მენიუ"
                >
                  <Menu className="w-5 h-5 text-slate-600" strokeWidth={1.7} />
                  {totalUnread > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
                  )}
                </button>
              ) : null
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default PageHeader;
